import { useMemo } from 'react';
import { useIdioma } from './index';

/**
 * Números e datas no idioma escolhido.
 *
 * Traduzir só o texto deixa a tela meio traduzida de um jeito que passa
 * despercebido em revisão: a frase sai em português e a data ao lado continua
 * `Aug 17, 2026`, o milhar continua `1,284` em vez de `1.284`. É o mesmo
 * defeito de uma string esquecida, só que ninguém procura por ele numa busca
 * por texto em inglês.
 *
 * O locale não é o código do idioma: `pt` sozinho resolve para Portugal em
 * alguns runtimes, e a data fica `17/08/2026` num formato que o Brasil escreve
 * igual mas o preço não. Daí a tabela explícita.
 *
 * Preço acompanha o idioma sem mudar de moeda. A cobrança é em dólar em
 * qualquer idioma — ver `conta.idiomaAjuda` —, e é o código `USD` que garante
 * isso: o locale decide só a pontuação e onde o símbolo entra. Em português
 * sai `US$ 79,00`, que diz mais claramente de que dólar se trata do que o
 * `$79.00` que estava fixo em todas as telas.
 *
 *     const { numero, data, dataHora, moeda } = useFormato();
 *     numero(1284)                  // "1,284" | "1.284"
 *     decimal(4.2667)               // "4.27" | "4,27"
 *     dataHora('2026-08-17T09:14Z') // "Aug 17, 2026, 06:14 AM" | "17 de ago. de 2026, 06:14"
 *     moeda(7900, 'usd')            // "$79.00" | "US$ 79,00"
 */

const LOCALE = { en: 'en-US', pt: 'pt-BR', es: 'es-ES' };

export const localeDe = (idioma) => LOCALE[idioma] || LOCALE.en;

/** Fora do provedor cai no inglês, como o `t`. */
export const useFormato = () => {
  const { idioma } = useIdioma();

  return useMemo(() => {
    const locale = localeDe(idioma);
    /* `undefined` e `null` viram zero: a alternativa é `NaN` na tela, e todo
       chamador aqui já tratava a ausência como zero antes disto existir. */
    const numero = (v) => Number(v || 0).toLocaleString(locale);
    /* Casas fixas, como o `toFixed`, mas com a vírgula do idioma: "4,27". */
    const decimal = (v, casas = 2) =>
      Number(v || 0).toLocaleString(locale, { minimumFractionDigits: casas, maximumFractionDigits: casas });
    const emData = (valor) => {
      /* `AAAA-MM-DD` é uma data, não um instante — mas o `Date` a interpreta
         como meia-noite UTC, e formatar isso em qualquer fuso negativo mostra
         o dia anterior. Um post datado de 24 aparecia como 23 no Brasil.
         Ancorar ao meio-dia local tira o problema em qualquer fuso, sem mexer
         em quem já passa data e hora. */
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(valor) ? `${valor}T12:00:00` : valor;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    return {
      locale,
      numero,
      decimal,
      /** Data curta. `vazio` é o que aparece quando o valor não é uma data. */
      data: (iso, opcoes, vazio = '—') => {
        const d = emData(iso);
        return d ? d.toLocaleDateString(locale, opcoes) : vazio;
      },
      dataHora: (iso, opcoes, vazio = '—') => {
        const d = emData(iso);
        return d ? d.toLocaleString(locale, opcoes) : vazio;
      },
      /** Centavos, como o Stripe entrega. `codigo` vem na própria cobrança. */
      moeda: (centavos, codigo) =>
        new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: (codigo || 'usd').toUpperCase()
        }).format((centavos || 0) / 100)
    };
  }, [idioma]);
};
